import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Alert,
} from "reactstrap";

import Section from "./Breadcrumb";
import { useSiteStore } from "../../../stores/Assets/site";
import { useOrderStore } from "../../../stores/Orders";
import moment from "moment";
import { DailyOrdersChart, DailyDeliveryTimeChart, ItemOrdersCandleChart } from "./Charts";

const NewReport = () => {
  document.title = "Orders per Day - Clickorder Admin";

  // State management
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [timezone, setTimezone] = useState(() => {
    // Capture user's timezone
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  });
  const [siteList, setSiteList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);

  // Store hooks
  const { getSites, sites } = useSiteStore((state) => state);
  const { statsLoading, getOrdersPerDayReport, ordersPerDayReport, isError, error: storeError } = useOrderStore((state) => state);

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const list = await getSites();
        const formattedOptions = list?.map((site) => ({
          label: `${site.site_id} - ${site.name}`,
          value: site.site_id,
        }));
        setSiteList(formattedOptions);
      } catch (err) {
        console.error("Error fetching sites:", err);
        setError("Failed to load sites");
      }
    };

    fetchSites();
  }, [getSites]);

  // Update site list when sites store changes and select first site by default
  useEffect(() => {
    const formattedOptions = sites?.map((site) => ({
      label: `${site.site_id} - ${site.name}`,
      value: site.site_id,
    }));
    setSiteList(formattedOptions);

    // Select first site by default if not already selected
    if (formattedOptions && formattedOptions.length > 0 && selectedSiteIds.length === 0) {
      setSelectedSiteIds([formattedOptions[0].value]);
    }
  }, [sites]);

  // Auto-generate report when first site is selected
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);

  useEffect(() => {
    const autoGenerateReport = async () => {
      if (selectedSiteIds.length > 0 && siteList.length > 0 && !hasAutoGenerated) {
        // Only auto-generate once when first site is selected
        setHasAutoGenerated(true);
        await getReport();
      }
    };

    autoGenerateReport();
  }, [selectedSiteIds, siteList]);

  // Handle store error state
  useEffect(() => {
    if (isError && storeError) {
      setError(storeError);
    }
  }, [isError, storeError]);

  // Get report data
  const getReport = async () => {
    setError(null);

    try {
      if (!startDate || !endDate) {
        setError("Please select a date range");
        return;
      }

      if (selectedSiteIds.length === 0) {
        setError("Please select at least one site");
        return;
      }

      // Call the store method with timezone
      const response = await getOrdersPerDayReport(selectedSiteIds, startDate, endDate, timezone);

      if (response) {
        // Transform API data to match frontend expectations
        const transformedData = {
          totalOrders: response.totalOrders || 0,
          minDeliveryTime: response.minDeliveryTime || 0,
          avgDeliveryTime: response.avgDeliveryTime || 0,
          maxDeliveryTime: response.maxDeliveryTime || 0,
          topSites: response.topSites ? response.topSites.map(site => ({
            siteId: site.site_id,
            siteName: site.site_name,
            orders: site.total_orders,
            revenue: site.total_revenue
          })) : [],
          dailyStats: response.dailyStats || [],
          itemStats: response.itemStats ? response.itemStats.map(item => ({
            item_name: item.item_name,
            order_count: item.order_count
          })) : [],
        };

        setReportData(transformedData);
      } else {
        setError("No data received from server");
      }
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Failed to fetch report data: " + (err.message || err));
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Format date range
  const formatDateRange = () => {
    if (!startDate || !endDate) return "";
    return `${moment(startDate).format("DD/MM/YYYY hh:mm")} - ${moment(endDate).format("DD/MM/YYYY hh:mm")}`;
  };


  // Format time in minutes and seconds
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <React.Fragment>
      <Container fluid>
        <Section
          getReport={getReport}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          timezone={timezone}
          setTimezone={setTimezone}
          selectedSiteIds={selectedSiteIds}
          setSelectedSiteIds={setSelectedSiteIds}
          siteList={siteList}
        />

        {error && (
          <Row className="mb-4">
            <Col xs={12}>
              <Alert color="danger">
                <i className="ri-error-warning-line me-2"></i>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Mock Data Notice */}
        {/* <Row className="mb-4">
          <Col xs={12}>
            <Alert color="info">
              <i className="ri-information-line me-2"></i>
              <strong>Demo Mode:</strong> This report is currently displaying mock data for demonstration purposes. 
              Select sites and date range, then click Filter to generate sample reports.
            </Alert>
          </Col>
        </Row> */}

        {siteList.length === 0 ? (
          <Row className="justify-content-center">
            <Col xs="auto">
              <div className="text-center">
                <Spinner color="primary" />
                <p className="mt-3 text-muted">Loading sites...</p>
              </div>
            </Col>
          </Row>
        ) : statsLoading ? (
          <Row className="justify-content-center">
            <Col xs="auto">
              <Spinner color="primary" />
              <span className="ms-2">Loading report data...</span>
            </Col>
          </Row>
        ) : reportData ? (
          <>
            {/* Summary Cards */}
            <Row className="mb-4">
              <Col xs={6} lg={3} md={6}>
                <Card className="card-animate">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0 small">
                          Total Orders
                        </p>
                      </div>
                      <div className="flex-shrink-0 d-none d-md-block">
                        <h5 className="text-success fs-14 mb-0">
                          <i className="ri-arrow-right-up-line fs-13 align-middle"></i>
                        </h5>
                      </div>
                    </div>
                    <div className="d-flex align-items-end justify-content-between mt-3">
                      <div>
                        <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                          {reportData.totalOrders}
                        </h4>
                      </div>
                      <div className="avatar-xs flex-shrink-0">
                        <span className="avatar-title rounded" style={{ backgroundColor: '#e8f5e8', fontSize: '1rem' }}>
                          <i className="ri-shopping-cart-line text-success"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xs={6} lg={3} md={6}>
                <Card className="card-animate">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0 small">
                          Min Delivery Time
                        </p>
                      </div>
                      <div className="flex-shrink-0 d-none d-md-block">
                        <h5 className="text-info fs-14 mb-0">
                          <i className="ri-time-line fs-13 align-middle"></i>
                        </h5>
                      </div>
                    </div>
                    <div className="d-flex align-items-end justify-content-between mt-3">
                      <div>
                        <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                          {formatTime(reportData.minDeliveryTime)}
                        </h4>
                      </div>
                      <div className="avatar-xs flex-shrink-0">
                        <span className="avatar-title rounded" style={{ backgroundColor: '#e3f2fd', fontSize: '1rem' }}>
                          <i className="ri-time-line text-info"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xs={6} lg={3} md={6}>
                <Card className="card-animate">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0 small">
                          Avg Delivery Time
                        </p>
                      </div>
                      <div className="flex-shrink-0 d-none d-md-block">
                        <h5 className="text-warning fs-14 mb-0">
                          <i className="ri-time-line fs-13 align-middle"></i>
                        </h5>
                      </div>
                    </div>
                    <div className="d-flex align-items-end justify-content-between mt-3">
                      <div>
                        <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                          {formatTime(reportData.avgDeliveryTime)}
                        </h4>
                      </div>
                      <div className="avatar-xs flex-shrink-0">
                        <span className="avatar-title rounded" style={{ backgroundColor: '#fff8e1', fontSize: '1rem' }}>
                          <i className="ri-time-line text-warning"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xs={6} lg={3} md={6}>
                <Card className="card-animate">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0 small">
                          Max Delivery Time
                        </p>
                      </div>
                      <div className="flex-shrink-0 d-none d-md-block">
                        <h5 className="text-danger fs-14 mb-0">
                          <i className="ri-time-line fs-13 align-middle"></i>
                        </h5>
                      </div>
                    </div>
                    <div className="d-flex align-items-end justify-content-between mt-3">
                      <div>
                        <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                          {formatTime(reportData.maxDeliveryTime)}
                        </h4>
                      </div>
                      <div className="avatar-xs flex-shrink-0">
                        <span className="avatar-title rounded" style={{ backgroundColor: '#ffebee', fontSize: '1rem' }}>
                          <i className="ri-time-line text-danger"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>


            {/* Daily Summary Section */}
            <Row className="mb-4">
              <Col lg={6}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">Daily Orders</h4>
                  </CardHeader>
                  <CardBody>
                    <DailyOrdersChart
                      dailyData={reportData.dailyStats}
                      dataColors='["#ce9558", "--vz-info", "--vz-success"]'
                    />
                  </CardBody>
                </Card>
              </Col>
              <Col lg={6}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">Daily Delivery Times</h4>
                  </CardHeader>
                  <CardBody>
                    <DailyDeliveryTimeChart
                      dailyData={reportData.dailyStats}
                      dataColors='["--vz-success","#ce9558" , "--vz-danger", "--vz-dark", "--vz-primary", "--vz-success", "--vz-secondary"]'
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Daily Summary Table */}
            <Row className="mb-4">
              <Col lg={12}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">Daily Summary Table</h4>
                  </CardHeader>
                  <CardBody>
                    <div className="table-responsive">
                      <table className="table table-nowrap align-middle table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Date</th>
                            <th scope="col">Total Orders</th>
                            <th scope="col">Min Delivery Time</th>
                            <th scope="col">Avg Delivery Time</th>
                            <th scope="col">Max Delivery Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.dailyStats.map((day, index) => (
                            <tr key={index}>
                              <td>
                                <span className="fw-medium">{moment(day.date).format('DD/MM/YYYY')}</span>
                              </td>
                              <td>
                                <span className="fw-medium">
                                  {day.total_orders}
                                </span>
                              </td>
                              <td>
                                <span className="fw-medium">
                                  {day.min_delivery_time ? formatTime(day.min_delivery_time) : 'N/A'}
                                </span>
                              </td>
                              <td>
                                <span className="fw-medium">
                                  {day.avg_delivery_time ? formatTime(day.avg_delivery_time) : 'N/A'}
                                </span>
                              </td>
                              <td>
                                <span className="fw-medium">
                                  {day.max_delivery_time ? formatTime(day.max_delivery_time) : 'N/A'}
                                </span>
                              </td>

                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Item Analysis Section */}
            <Row>
              <Col lg={4}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">Item Order Count</h4>
                  </CardHeader>
                  <CardBody>
                    <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
                      <table className="table table-nowrap align-middle table-hover mb-0">
                        <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                          <tr>
                            <th scope="col">Item Name</th>
                            <th scope="col">Orders</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.itemStats.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar-xs me-2">
                                    <span className="avatar-title  rounded" style={{ backgroundColor: "#ce9558" }}>
                                      {index + 1}
                                    </span>
                                  </div>
                                  <div>
                                    <h6 className="mb-0">{item.item_name}</h6>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-info-subtle text-primary" style={{ fontSize: "15px" }}>
                                  {item.order_count}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={8}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">Item Order Distribution</h4>
                  </CardHeader>
                  <CardBody>
                    <ItemOrdersCandleChart
                      itemData={reportData.itemStats}
                      dataColors='["#ce9558" , "--vz-info", "--vz-success", "--vz-warning", "--vz-danger", "--vz-dark", "--vz-secondary", "--vz-light"]'
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Row className="justify-content-center">
            <Col xs="auto">
              <div className="text-center">
                <i className="ri-file-chart-line text-muted" style={{ fontSize: "4rem" }}></i>
                <h5 className="mt-3 text-muted">No data to display</h5>
                <p className="text-muted">Click Filter to generate report for the selected site</p>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </React.Fragment>
  );
};

export default NewReport;
